import { useCallback, useEffect, useRef, useState } from "react";
import {
  BookOpen, ChevronLeft, Download, Ellipsis, Layers, Maximize, Minus, Plus, Presentation, Redo2, Share2, Undo2, Grid3x3
} from "lucide-react";
import { useCamera, useEditor, useHistoryState, useStore, useUi } from "../editor/react/hooks";
import { IconButton, Panel, Popover } from "../ui/kit";

export type SyncState = "saved" | "saving" | "offline" | "local" | "error" | "view" | "connecting";

// Saving happens quietly in the background. The title card only speaks up about things worth knowing:
// what kind of board this is, or a connection problem that has lasted a while (not every brief blip).
const SYNC_LABEL: Partial<Record<SyncState, string>> = {
  offline: "offline · kept on this device",
  local: "on this device only",
  error: "couldn't save — retrying",
  view: "view only"
};
const PATIENT: ReadonlySet<SyncState> = new Set(["offline", "error"]);

function useShownSync(sync: SyncState): SyncState | null {
  const [shown, setShown] = useState<SyncState | null>(() => (SYNC_LABEL[sync] && !PATIENT.has(sync) ? sync : null));
  useEffect(() => {
    if (!SYNC_LABEL[sync]) return setShown(null);
    if (!PATIENT.has(sync)) return setShown(sync);
    const t = window.setTimeout(() => setShown(sync), 5000);
    return () => window.clearTimeout(t);
  }, [sync]);
  return shown;
}

export function TitleCard({ title, onTitle, sync, onBack, backLabel, editable }: { title: string; onTitle: (t: string) => void; sync: SyncState; onBack: () => void; backLabel: string; editable: boolean }) {
  const [draft, setDraft] = useState(title);
  const focused = useRef(false);
  const shownSync = useShownSync(sync);
  useEffect(() => {
    if (!focused.current) setDraft(title);
  }, [title]);
  return (
    <Panel className="title-card" seed="titlecard" tape="#e7a4b4" tapeSide="left">
      <button type="button" className="back-btn" onClick={onBack} aria-label={backLabel} data-tip={backLabel} data-tip-side="bottom">
        <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
        <img src="/favicon-64.png" alt="" width="26" height="26" />
        <span className="wordmark">sepia</span>
      </button>
      <span className="grip" aria-hidden="true" />
      <input
        className="board-title"
        value={draft}
        readOnly={!editable}
        aria-label="Board title"
        onFocus={() => (focused.current = true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          focused.current = false;
          const t = draft.trim() || "Untitled board";
          setDraft(t);
          if (t !== title) onTitle(t);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter" || e.key === "Escape") (e.target as HTMLInputElement).blur();
        }}
        size={Math.max(8, Math.min(28, draft.length + 1))}
      />
      {shownSync && <span className={"sync sync-" + shownSync}>{SYNC_LABEL[shownSync]}</span>}
    </Panel>
  );
}

export interface MenuItem {
  label: string;
  onClick: () => void;
  hint?: string;
  danger?: boolean;
  divider?: boolean;
}

export function ActionBar({ onShare, onExport, onPresent, onPages, onLayers, layersOpen, menu, canShare }: { onShare: () => void; onExport: () => void; onPresent: () => void; onPages: () => void; onLayers: () => void; layersOpen: boolean; menu: MenuItem[]; canShare: boolean }) {
  const ed = useEditor();
  const { canUndo, canRedo } = useHistoryState();
  const readOnly = useUi((ui) => ui.readOnly);
  const [menuOpen, setMenuOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  return (
    <Panel className="action-bar" seed="actions">
      {!readOnly && (
        <>
          <IconButton icon={Undo2} label="Undo" shortcut="⌘Z · two-finger tap" onClick={() => ed.undo()} disabled={!canUndo} />
          <IconButton icon={Redo2} label="Redo" shortcut="⇧⌘Z · three-finger tap" onClick={() => ed.redo()} disabled={!canRedo} />
          <span className="grip" aria-hidden="true" />
        </>
      )}
      <IconButton icon={Layers} label="Layers" shortcut="⇧⌘N new layer" active={layersOpen} onClick={onLayers} />
      <IconButton icon={BookOpen} label="Pages & links" onClick={onPages} />
      <button type="button" className="btn btn-plain bar-btn" onClick={onPresent}>
        <Presentation size={17} strokeWidth={1.9} aria-hidden="true" /> <span>Present</span>
      </button>
      {canShare && (
        <button type="button" className="btn btn-plain bar-btn" onClick={onShare}>
          <Share2 size={17} strokeWidth={1.9} aria-hidden="true" /> <span>Share</span>
        </button>
      )}
      <button type="button" className="btn btn-primary bar-btn export-btn" onClick={onExport}>
        <Download size={17} strokeWidth={2} aria-hidden="true" /> <span>Export</span>
      </button>
      <IconButton ref={moreRef} icon={Ellipsis} label="More" onClick={() => setMenuOpen((o) => !o)} />
      <Popover anchor={moreRef.current} open={menuOpen} onClose={() => setMenuOpen(false)} align="end" className="menu-pop">
        <div className="menu" role="menu">
          {menu.map((m, i) =>
            m.divider ? (
              <hr key={i} />
            ) : (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={"menu-item" + (m.danger ? " danger" : "")}
                onClick={() => {
                  setMenuOpen(false);
                  m.onClick();
                }}
              >
                <span>{m.label}</span>
                {m.hint && <kbd>{m.hint}</kbd>}
              </button>
            )
          )}
        </div>
      </Popover>
    </Panel>
  );
}

/** Which page of a notebook is in view ("2 of 5"); null on an endless board. */
function usePageInView(): { n: number; total: number } | null {
  const ed = useEditor();
  const sub = useCallback(
    (fn: () => void) => {
      const offs = [ed.camera.subscribe(fn), ed.doc.subscribe((c) => c.structural && fn())];
      return () => offs.forEach((o) => o());
    },
    [ed]
  );
  return useStore(sub, () => {
    const pages = ed.pages();
    const cur = ed.currentPage();
    return pages.length && cur ? { n: pages.indexOf(cur) + 1, total: pages.length } : null;
  });
}

export function ZoomBar({ onBackground, onPages }: { onBackground: () => void; onPages: () => void }) {
  const ed = useEditor();
  const z = useCamera((c) => c.z);
  const page = usePageInView();
  return (
    <Panel className="zoom-bar" seed="zoom">
      <IconButton icon={Minus} size={17} label="Zoom out" shortcut="⌘−" onClick={() => ed.zoomBy(1 / 1.25)} tipSide="top" />
      <button type="button" className="zoom-level" onClick={() => ed.resetZoom()} data-tip="Reset to 100% · ⌘0" data-tip-side="top">
        {Math.round(z * 100)}%
      </button>
      <IconButton icon={Plus} size={17} label="Zoom in" shortcut="⌘+" onClick={() => ed.zoomBy(1.25)} tipSide="top" />
      <IconButton icon={Maximize} size={17} label={page ? "Fit the page" : "Fit everything"} shortcut="⇧1" onClick={() => ed.zoomToFit()} tipSide="top" />
      <span className="grip" aria-hidden="true" />
      {page && (
        <>
          <button type="button" className="page-pill" onClick={onPages} data-tip="Pages: add, reorder, jump" data-tip-side="top" aria-label={`Page ${page.n} of ${page.total}. Open pages`}>
            {page.n}<span>/{page.total}</span>
          </button>
          <span className="grip" aria-hidden="true" />
        </>
      )}
      <IconButton icon={Grid3x3} size={17} label={page ? "Paper & ruling" : "Paper & grid"} onClick={onBackground} tipSide="top" />
    </Panel>
  );
}
